import {Card, Divider} from '@albert-io/atomic';
import {GuessModelV1, QuestionModelV3, QuestionSetModelV1, SubjectModelV2} from '@albert-io/models';
import classNames from 'classnames';
import SplitPane from 'client/components/SplitPane/SplitPane.react';
import {debounce} from 'lodash';
import React, {useEffect, useRef, useState} from 'react';
import {useShallow} from 'zustand/react/shallow';

import CopyrightModal from 'client/components/CopyrightModal/CopyrightModal.react';
import {useIsFeatureFlagEnabled} from 'client/components/FeatureFlag/featureFlag.utils';

import DraftGuessWrapper from '../../../../AssignmentPracticeView/DraftGuesses/DraftGuessWrapper';

import {
  Explanation,
  GuessCorrectnessBanner,
  InteractivityHandler,
  Prompt,
  Standards,
  StandardsTable,
  Tags
} from '../ContentBlocks';

import QuestionTypeStore from '../../QuestionType.store';
import {HeadingBar} from '../HeadingBar/HeadingBar.react';
import HowOthersAnswered from '../HowOthersAnswered';
import PostSubmissionScrollPoint from '../PostSubmissionScrollPoint/PostSubmissionScrollPoint';
import '../QuestionWrapper/question-wrapper.scss';
import {
  TranslationHeader,
  useQuestionTranslationStore,
  useShowTranslationToggle
} from '../TranslationSection';
import './question-component.scss';

import {ArchivedQuestionBanner} from 'client/components/ArchivedQuestions/ArchivedQuestionBanner.react';
import CONSTANTS from 'client/constants';

import ReviewAnswersInEnglishBanner from 'client/ReviewAnswersInEnglishBanner';

interface PreGuessQuestionContentProps {
  activeQuestion: QuestionModelV3;
  activeQuestionStore?: typeof QuestionTypeStore;
  ActiveQuestionInputArea: React.ComponentType<any>;
  interactive?: boolean;
  isDennisQuestionPreviewUpdatedView?: boolean;
}

const PreGuessQuestionContent = ({
  activeQuestion,
  activeQuestionStore,
  ActiveQuestionInputArea,
  interactive,
  isDennisQuestionPreviewUpdatedView = false
}: PreGuessQuestionContentProps) => {
  return (
    <DraftGuessWrapper
      storeName={activeQuestion.getStoreName()}
      submitted={false}
      store={activeQuestionStore}
    >
      <InteractivityHandler interactive={interactive}>
        <ActiveQuestionInputArea
          isGuessSubmitted={false}
          isGuessCorrect={false}
          storeName={activeQuestion.getStoreName()}
          question={activeQuestion}
          questionId={activeQuestion.getId()}
          isDennisQuestionPreviewUpdatedView={isDennisQuestionPreviewUpdatedView}
        />
      </InteractivityHandler>
    </DraftGuessWrapper>
  );
};

interface PostGuessQuestionContentProps {
  activeQuestion: QuestionModelV3;
  activeQuestionSet: QuestionSetModelV1;
  activeQuestionStore?: typeof QuestionTypeStore;
  activeSubject: SubjectModelV2;
  ActiveQuestionInputArea: React.ComponentType<any>;
  customExplanation?: React.ReactElement; // custom explanation component
  customHOA?: React.ReactElement; // custom how others answered component
  customResultIndicator?: React.ReactElement; // custom result indicator component
  isRedesign?: boolean;
  guess?: GuessModelV1;
  postSubmissionScrollPointRef?: React.RefObject<any>;
  partialCreditEnabled?: boolean;
  isDennisQuestionPreviewUpdatedView?: boolean;
}

const PostGuessQuestionContent = ({
  activeQuestion,
  activeQuestionSet,
  activeSubject,
  ActiveQuestionInputArea,
  customExplanation,
  customHOA,
  customResultIndicator,
  guess,
  isRedesign,
  postSubmissionScrollPointRef,
  partialCreditEnabled = false,
  isDennisQuestionPreviewUpdatedView = false
}: PostGuessQuestionContentProps) => {
  return (
    <DraftGuessWrapper storeName={activeQuestion.getStoreName()} submitted>
      <ActiveQuestionInputArea
        isGuessSubmitted
        isGuessCorrect={guess?.isCorrect()}
        storeName={activeQuestion.getStoreName()}
        question={activeQuestion}
        questionId={activeQuestion.getId()}
        isDennisQuestionPreviewUpdatedView={isDennisQuestionPreviewUpdatedView}
      />
      <PostSubmissionScrollPoint ref={postSubmissionScrollPointRef} />
      {customResultIndicator ?? (
        <GuessCorrectnessBanner guess={guess} partialCreditEnabled={partialCreditEnabled} />
      )}
      {customExplanation ?? <Explanation question={activeQuestion} />}
      {customHOA ?? <HowOthersAnswered question={activeQuestion} />}
      {!isRedesign && activeSubject.isShouldShowLabelTable() && (
        <StandardsTable question={activeQuestion} />
      )}
      {!isRedesign && (
        <Standards
          question={activeQuestion}
          subject={activeSubject}
          questionSet={activeQuestionSet}
        />
      )}
      {!isRedesign && (
        <Tags question={activeQuestion} subject={activeSubject} questionSet={activeQuestionSet} />
      )}
    </DraftGuessWrapper>
  );
};

interface DennisQuestionContentProps {
  activeQuestion: QuestionModelV3;
  activeQuestionStore?: typeof QuestionTypeStore;
  ActiveQuestionInputArea: React.ComponentType<any>;
  interactive?: boolean;
  isDennisQuestionPreviewUpdatedView?: boolean;
}

const DennisQuestionContent = ({
  activeQuestion,
  activeQuestionStore,
  ActiveQuestionInputArea,
  interactive,
  isDennisQuestionPreviewUpdatedView = false
}: DennisQuestionContentProps) => {
  return (
    <InteractivityHandler interactive={interactive}>
      <ActiveQuestionInputArea
        storeName={activeQuestionStore?.getName?.()}
        question={activeQuestion}
        questionId={activeQuestion.getId()}
        isDennisQuestionPreviewUpdatedView={isDennisQuestionPreviewUpdatedView}
        activeQuestionStore={activeQuestionStore}
      />
    </InteractivityHandler>
  );
};

interface QuestionContentProps {
  questionState: 'PreGuess' | 'PostGuess' | 'Dennis';
}

type CombinedProps = QuestionContentProps &
  (PreGuessQuestionContentProps | PostGuessQuestionContentProps | DennisQuestionContentProps);
const QuestionContent = (props: CombinedProps) => {
  const {questionState, ...rest} = props;

  if (questionState === 'PreGuess') {
    const {
      activeQuestion,
      activeQuestionStore,
      ActiveQuestionInputArea,
      interactive,
      isDennisQuestionPreviewUpdatedView
    } = rest as PreGuessQuestionContentProps;
    return (
      <PreGuessQuestionContent
        activeQuestion={activeQuestion}
        activeQuestionStore={activeQuestionStore}
        ActiveQuestionInputArea={ActiveQuestionInputArea}
        interactive={interactive}
        isDennisQuestionPreviewUpdatedView={isDennisQuestionPreviewUpdatedView}
      />
    );
  }

  if (questionState === 'Dennis') {
    const {
      activeQuestion,
      activeQuestionStore,
      ActiveQuestionInputArea,
      interactive,
      isDennisQuestionPreviewUpdatedView
    } = rest as DennisQuestionContentProps;
    return (
      <DennisQuestionContent
        activeQuestion={activeQuestion}
        activeQuestionStore={activeQuestionStore}
        ActiveQuestionInputArea={ActiveQuestionInputArea}
        interactive={interactive}
        isDennisQuestionPreviewUpdatedView={isDennisQuestionPreviewUpdatedView}
      />
    );
  }

  const {
    activeQuestion,
    activeQuestionSet,
    activeQuestionStore,
    activeSubject,
    ActiveQuestionInputArea,
    customExplanation,
    customHOA,
    customResultIndicator,
    guess,
    isRedesign,
    postSubmissionScrollPointRef,
    partialCreditEnabled = false,
    isDennisQuestionPreviewUpdatedView
  } = rest as PostGuessQuestionContentProps;
  return (
    <PostGuessQuestionContent
      activeQuestion={activeQuestion}
      activeQuestionSet={activeQuestionSet}
      activeQuestionStore={activeQuestionStore}
      activeSubject={activeSubject}
      ActiveQuestionInputArea={ActiveQuestionInputArea}
      customExplanation={customExplanation}
      customHOA={customHOA}
      customResultIndicator={customResultIndicator}
      guess={guess}
      isRedesign={isRedesign}
      postSubmissionScrollPointRef={postSubmissionScrollPointRef}
      partialCreditEnabled={partialCreditEnabled}
      isDennisQuestionPreviewUpdatedView={isDennisQuestionPreviewUpdatedView}
    />
  );
};

export interface QuestionComponentProps
  extends
    QuestionContentProps,
    PreGuessQuestionContentProps,
    PostGuessQuestionContentProps,
    DennisQuestionContentProps {
  actions: React.ReactElement;
  answerInputPaneRef?: React.RefObject<HTMLDivElement>;
  customHeader?: React.ReactNode;
  displayOrientation: 'default' | 'vertical' | 'horizontal';
  hasHeading?: boolean;
  hasQuestionSetAccessPredicate: (questionSet: QuestionSetModelV1) => boolean;
  headingActions: React.ReactNode;
  isMobile?: boolean;
  modifyTitle?: (args: any) => void;
  navigateBackProps?: {
    disabled: boolean;
    handleNavigation: () => void;
  };
  navigateNextProps?: {
    disabled: boolean;
    handleNavigation: () => void;
  };
  resizerPosition?: number;
  onResize?: (size: number) => void;
  showDifficulty: boolean;
  stackedContainerRef?: React.RefObject<HTMLDivElement>;
  partialCreditEnabled?: boolean;
  shouldSetContentMaxWidth?: boolean;
  isDennisQuestionPreviewUpdatedView?: boolean;
  useAdaptiveResizer?: boolean;
  shouldShowCopyright?: boolean;
}

type QuestionComponentContentSplitProps = Omit<
  QuestionComponentProps,
  'displayOrientation' | 'headingActions' | 'showDifficulty'
>;

const PANE_MIN_WIDTH = 280;
const CONTAINER_PADDING = 32;

export const questionStemRegex = /\[question-stem\]([\s\S]*?)\[\/question-stem\]/;
// remove question stem from left, only show question stem on right
const transformLeftText = (text) => text.replace(questionStemRegex, '');
const transformRightText = (text) => text.match(questionStemRegex)?.[1] ?? '';

const QuestionComponentContentSplit = ({
  actions,
  activeQuestion,
  activeQuestionSet,
  activeQuestionStore,
  activeSubject,
  ActiveQuestionInputArea,
  answerInputPaneRef,
  customExplanation,
  customHOA,
  customResultIndicator,
  guess,
  hasQuestionSetAccessPredicate,
  interactive,
  navigateBackProps,
  navigateNextProps,
  onResize,
  postSubmissionScrollPointRef,
  questionState,
  resizerPosition,
  partialCreditEnabled = false,
  shouldSetContentMaxWidth,
  isDennisQuestionPreviewUpdatedView = false,
  useAdaptiveResizer,
  shouldShowCopyright
}: QuestionComponentContentSplitProps) => {
  const splitPaneRef = useRef<HTMLDivElement | null>(null);
  // using the width of the split pane to determine the max size of the left pane (in other words, enforcing the min size for the right pane)
  // falling back on using the viewport width minus padding since we can assume that the split pane will be close to the full width of the viewport
  const [elementSize, setElementSize] = useState<number>(window.innerWidth - CONTAINER_PADDING);

  const {showTranslations} = useQuestionTranslationStore(
    useShallow((state) => ({
      showTranslations: state.showTranslations
    }))
  );

  useEffect(() => {
    const handleResize = () => {
      const newSize =
        splitPaneRef.current?.getBoundingClientRect().width ??
        window.innerWidth - CONTAINER_PADDING;
      setElementSize(newSize);
    };

    handleResize();

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  });

  const showTranslationHeader = useShowTranslationToggle();
  const {isFeatureEnabled: isNewTranslationToggleEnabled} =
    useIsFeatureFlagEnabled('new_translation_toggle');

  return (
    <div
      ref={splitPaneRef}
      className={classNames('split-content-wrapper h-full', {
        'flex flex-col': showTranslationHeader
      })}
    >
      {!isNewTranslationToggleEnabled && showTranslationHeader && <TranslationHeader />}
      <SplitPane
        allowResize
        primary='first'
        split='vertical'
        maxSize={elementSize - PANE_MIN_WIDTH}
        minSize={PANE_MIN_WIDTH}
        defaultSize={resizerPosition}
        onChange={debounce((size) => onResize?.(size), 100)}
        style={{position: 'relative'}}
        resizerClassName={classNames({
          'resizer-grabber question-component-resizer': !useAdaptiveResizer,
          'adaptive-question-resizer': useAdaptiveResizer
        })}
        pane1Style={{display: 'flex', flexDirection: 'column', overflow: 'auto'}}
        pane2Style={{display: 'flex', flexDirection: 'column', overflow: 'auto'}}
      >
        <Card
          className={classNames('question-wrapper question-split-pane instructions-pane', {
            'with-translations': showTranslationHeader && !isNewTranslationToggleEnabled
          })}
          roundness={showTranslationHeader ? 'none' : 'small'}
        >
          <div
            className={classNames('question-wrapper__body p-x4 xs:p-x2', {
              'mx-auto! w-full max-w-[600px]!': shouldSetContentMaxWidth
            })}
          >
            <Prompt question={activeQuestion} transformText={transformLeftText} />
          </div>
        </Card>
        <Card
          as='form'
          className={classNames('question-wrapper question-split-pane answer-options-pane', {
            'with-translations': showTranslationHeader && !isNewTranslationToggleEnabled
          })}
          roundness={showTranslationHeader ? 'none' : 'small'}
          // @ts-ignore: TS complains about onSubmit not being a valid prop for a `form` Card
          onSubmit={(e) => {
            e.preventDefault();
          }}
        >
          <div
            ref={answerInputPaneRef}
            className='question-wrapper__body flex flex-col justify-between'
          >
            {/* change padding when translation toggle is available */}

            <div
              className={classNames('p-x4 xs:p-x2', {
                'mt-x4': showTranslationHeader && !showTranslations,
                'mt-x8': showTranslationHeader && showTranslations,
                'mx-auto! w-full max-w-[600px]!': shouldSetContentMaxWidth
              })}
            >
              {activeQuestion?.getPrompt()?.match(questionStemRegex) && (
                <div>
                  <Prompt question={activeQuestion} transformText={transformRightText} />
                  <Divider spacing={4} />
                </div>
              )}
              {isNewTranslationToggleEnabled && showTranslations && (
                <ReviewAnswersInEnglishBanner className='py-x1' />
              )}
              <QuestionContent
                activeQuestion={activeQuestion}
                activeQuestionSet={activeQuestionSet}
                activeQuestionStore={activeQuestionStore}
                activeSubject={activeSubject}
                ActiveQuestionInputArea={ActiveQuestionInputArea}
                customExplanation={customExplanation}
                customHOA={customHOA}
                customResultIndicator={customResultIndicator}
                guess={guess}
                interactive={interactive}
                isRedesign
                postSubmissionScrollPointRef={postSubmissionScrollPointRef}
                questionState={questionState}
                partialCreditEnabled={partialCreditEnabled}
                isDennisQuestionPreviewUpdatedView={isDennisQuestionPreviewUpdatedView}
              />
            </div>
            {shouldShowCopyright && <CopyrightModal />}
            {actions &&
              React.cloneElement(actions, {
                guess,
                hasAccess: hasQuestionSetAccessPredicate?.(activeQuestionSet) || false,
                navigateBackProps,
                navigateNextProps,
                question: activeQuestion,
                questionSet: activeQuestionSet,
                subject: activeSubject
              })}
          </div>
        </Card>
      </SplitPane>
    </div>
  );
};

const QuestionComponentHorizontalLayout = ({
  actions,
  activeQuestion,
  activeQuestionSet,
  activeQuestionStore,
  activeSubject,
  ActiveQuestionInputArea,
  customExplanation,
  customHOA,
  customResultIndicator,
  guess,
  hasQuestionSetAccessPredicate,
  interactive,
  navigateBackProps,
  navigateNextProps,
  postSubmissionScrollPointRef,
  questionState,
  stackedContainerRef,
  partialCreditEnabled,
  isDennisQuestionPreviewUpdatedView,
  shouldSetContentMaxWidth,
  shouldShowCopyright
}: QuestionComponentContentSplitProps) => {
  const showTranslationHeader = useShowTranslationToggle();
  const {isFeatureEnabled: isNewTranslationToggleEnabled} =
    useIsFeatureFlagEnabled('new_translation_toggle');

  const {showTranslations} = useQuestionTranslationStore(
    useShallow((state) => ({
      showTranslations: state.showTranslations && state.translationSettings.question
    }))
  );
  return (
    <div ref={stackedContainerRef} className='question-component__horizontal-layout-wrapper'>
      {questionState !== 'Dennis' && !isNewTranslationToggleEnabled && showTranslationHeader && (
        <TranslationHeader />
      )}
      <div
        className={classNames('question-component__horizontal-layout__question-wrapper', {
          'grid! place-items-center': shouldSetContentMaxWidth
        })}
      >
        <div
          className={classNames({
            'w-full!': shouldSetContentMaxWidth
          })}
        >
          <Card
            roundness='none'
            className={classNames('question-wrapper horizontal-layout__instructions-card', {
              'w-full! border-none!': shouldSetContentMaxWidth,
              'with-translations':
                showTranslationHeader &&
                !isNewTranslationToggleEnabled &&
                questionState !== 'Dennis'
            })}
          >
            <div
              className={classNames('question-wrapper__body p-x4 xs:p-x2', {
                'w-full max-w-[600px]!': shouldSetContentMaxWidth
              })}
            >
              {/* change padding when translation toggle is avialbe */}
              <Prompt question={activeQuestion} transformText={transformLeftText} />
            </div>
          </Card>
          <Card
            as='form'
            className={classNames('question-wrapper horizontal-layout__answer-options-card', {
              'w-full! border-none!': shouldSetContentMaxWidth
            })}
            // @ts-ignore: TS complains about onSubmit not being a valid prop for a `form` Card
            onSubmit={(e) => {
              e.preventDefault();
            }}
          >
            <div
              className={classNames('question-wrapper__body', {
                'max-w-[600px]': shouldSetContentMaxWidth
              })}
            >
              {activeQuestionStore && (
                <div className='p-x4 xs:p-x2'>
                  {activeQuestion?.getPrompt()?.match(questionStemRegex) && (
                    <div>
                      <Prompt question={activeQuestion} transformText={transformRightText} />
                      <Divider spacing={4} />
                    </div>
                  )}
                  {isNewTranslationToggleEnabled && showTranslations && (
                    <ReviewAnswersInEnglishBanner className='py-x1' />
                  )}
                  <QuestionContent
                    activeQuestion={activeQuestion}
                    activeQuestionSet={activeQuestionSet}
                    activeQuestionStore={activeQuestionStore}
                    activeSubject={activeSubject}
                    ActiveQuestionInputArea={ActiveQuestionInputArea}
                    customExplanation={customExplanation}
                    customHOA={customHOA}
                    customResultIndicator={customResultIndicator}
                    guess={guess}
                    interactive={interactive}
                    isRedesign
                    postSubmissionScrollPointRef={postSubmissionScrollPointRef}
                    questionState={questionState}
                    partialCreditEnabled={partialCreditEnabled}
                    isDennisQuestionPreviewUpdatedView={isDennisQuestionPreviewUpdatedView}
                  />
                </div>
              )}
              {shouldShowCopyright && <CopyrightModal />}
              {actions &&
                React.cloneElement(actions, {
                  guess,
                  hasAccess: hasQuestionSetAccessPredicate?.(activeQuestionSet) || false,
                  navigateBackProps,
                  navigateNextProps,
                  question: activeQuestion,
                  questionSet: activeQuestionSet,
                  subject: activeSubject
                })}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export const QuestionComponent = ({
  actions,
  activeQuestion,
  activeQuestionSet,
  activeQuestionStore,
  activeSubject,
  answerInputPaneRef,
  ActiveQuestionInputArea,
  customExplanation,
  customHeader,
  customHOA,
  customResultIndicator,
  displayOrientation,
  guess,
  hasHeading = true,
  hasQuestionSetAccessPredicate,
  headingActions,
  interactive,
  isMobile,
  modifyTitle,
  navigateBackProps,
  navigateNextProps,
  onResize,
  postSubmissionScrollPointRef,
  questionState,
  resizerPosition,
  showDifficulty,
  stackedContainerRef,
  partialCreditEnabled,
  isDennisQuestionPreviewUpdatedView,
  shouldSetContentMaxWidth = false,
  useAdaptiveResizer = false,
  shouldShowCopyright = true
}: QuestionComponentProps) => {
  const {init, reset, isReady, fetchTranslation, showTranslations} = useQuestionTranslationStore(
    useShallow((state) => ({
      init: state.init,
      reset: state.reset,
      fetchTranslation: state.fetchTranslation,
      isReady: state.isReady,
      showTranslations: state.showTranslations
    }))
  );

  const {isFeatureEnabled: isNewTranslationToggleEnabled} =
    useIsFeatureFlagEnabled('new_translation_toggle');

  const isArchivedQuestion = activeQuestion['status'] === CONSTANTS.QUESTIONSET_STATUSES.ARCHIVED;

  const [shouldShowHeader, setShouldShowHeader] = useState(hasHeading);

  // initialize translation store, check if translation toggle should be shown
  useEffect(() => {
    if (!isReady) {
      const params = global.location.pathname.split('/');

      let assignmentId: string | null = null;
      let isAdaptive = false;

      if (params.some((param) => param === 'assignment')) {
        assignmentId = params[params.length - 1];
        setShouldShowHeader(false);
      }

      if (params.some((param) => param === 'adaptive')) {
        isAdaptive = true;
      }

      init(assignmentId, isAdaptive);
    }
  }, [init, isReady]);

  useEffect(() => {
    if (!activeQuestion?.getId()) {
      return;
    }

    // the translation store handles early returning if translations are not meant to be shown
    // safe to call here
    fetchTranslation(activeQuestion, activeSubject);
  }, [activeQuestion, fetchTranslation, activeQuestionSet, activeSubject]);

  useEffect(() => {
    return () => reset();
  }, [reset]);

  // header should never show in AB
  useEffect(() => {
    const params = global.location.pathname.split('/');

    if (params.some((param) => param === 'assignment-builder')) {
      setShouldShowHeader(false);
    }
  }, []);

  // -------- Student views ------------

  if (displayOrientation === 'vertical' && !isMobile) {
    return (
      <QuestionComponentContentSplit
        actions={actions}
        activeQuestion={activeQuestion}
        activeQuestionSet={activeQuestionSet}
        activeQuestionStore={activeQuestionStore}
        activeSubject={activeSubject}
        ActiveQuestionInputArea={ActiveQuestionInputArea}
        answerInputPaneRef={answerInputPaneRef}
        customExplanation={customExplanation}
        customHOA={customHOA}
        customResultIndicator={customResultIndicator}
        guess={guess}
        hasQuestionSetAccessPredicate={hasQuestionSetAccessPredicate}
        interactive={interactive}
        isMobile={isMobile}
        navigateBackProps={navigateBackProps}
        navigateNextProps={navigateNextProps}
        onResize={onResize}
        postSubmissionScrollPointRef={postSubmissionScrollPointRef}
        questionState={questionState}
        resizerPosition={resizerPosition}
        partialCreditEnabled={partialCreditEnabled}
        shouldSetContentMaxWidth={shouldSetContentMaxWidth}
        useAdaptiveResizer={useAdaptiveResizer}
        shouldShowCopyright={shouldShowCopyright}
      />
    );
  }

  if (
    (displayOrientation === 'horizontal' && !isMobile) ||
    (isMobile && displayOrientation !== 'default')
  ) {
    return (
      <QuestionComponentHorizontalLayout
        actions={actions}
        activeQuestion={activeQuestion}
        activeQuestionSet={activeQuestionSet}
        activeQuestionStore={activeQuestionStore}
        activeSubject={activeSubject}
        ActiveQuestionInputArea={ActiveQuestionInputArea}
        customExplanation={customExplanation}
        customHOA={customHOA}
        customResultIndicator={customResultIndicator}
        guess={guess}
        hasQuestionSetAccessPredicate={hasQuestionSetAccessPredicate}
        interactive={interactive}
        isMobile={isMobile}
        navigateBackProps={navigateBackProps}
        navigateNextProps={navigateNextProps}
        postSubmissionScrollPointRef={postSubmissionScrollPointRef}
        questionState={questionState}
        stackedContainerRef={stackedContainerRef}
        partialCreditEnabled={partialCreditEnabled}
        isDennisQuestionPreviewUpdatedView={isDennisQuestionPreviewUpdatedView}
        shouldSetContentMaxWidth={shouldSetContentMaxWidth}
        shouldShowCopyright={shouldShowCopyright}
      />
    );
  }

  return (
    <Card
      as='form'
      className='question-wrapper'
      // @ts-ignore: TS complains about onSubmit not being a valid prop for a `form` Card
      onSubmit={(e) => {
        e.preventDefault();
      }}
    >
      <div className='question-wrapper__heading'>
        {shouldShowHeader && (
          <>
            <HeadingBar
              question={activeQuestion}
              questionSet={activeQuestionSet}
              navigateBackProps={navigateBackProps}
              navigateNextProps={navigateNextProps}
              headingActions={headingActions}
              modifyTitle={modifyTitle}
              showDifficulty={showDifficulty}
              customHeader={customHeader}
            />
            {!isNewTranslationToggleEnabled && <TranslationHeader inLegacyView />}
            {/* <TranslationHeader inLegacyView /> */}
          </>
        )}
      </div>
      <div className='question-wrapper__body'>
        <div
          className={classNames('p-x4 xs:p-x2', {
            'mt-x2': showTranslations && !isNewTranslationToggleEnabled
          })}
        >
          {isArchivedQuestion && <ArchivedQuestionBanner classname='mb-x4' />}
          <Prompt question={activeQuestion} />
          {isNewTranslationToggleEnabled && showTranslations && (
            <ReviewAnswersInEnglishBanner className='pb-x4' />
          )}
          <QuestionContent
            activeQuestion={activeQuestion}
            activeQuestionSet={activeQuestionSet}
            activeQuestionStore={activeQuestionStore}
            activeSubject={activeSubject}
            ActiveQuestionInputArea={ActiveQuestionInputArea}
            customExplanation={customExplanation}
            customHOA={customHOA}
            customResultIndicator={customResultIndicator}
            guess={guess}
            interactive={interactive}
            isRedesign={false}
            postSubmissionScrollPointRef={postSubmissionScrollPointRef}
            questionState={questionState}
            partialCreditEnabled={partialCreditEnabled}
            isDennisQuestionPreviewUpdatedView={isDennisQuestionPreviewUpdatedView}
          />
        </div>
        {actions &&
          React.cloneElement(actions, {
            guess,
            hasAccess: hasQuestionSetAccessPredicate?.(activeQuestionSet) || false,
            navigateBackProps,
            navigateNextProps,
            question: activeQuestion,
            questionSet: activeQuestionSet,
            subject: activeSubject
          })}
      </div>
    </Card>
  );
};
